import { useEffect, useMemo } from 'react';
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import SvgColor from 'src/components/svg-color';
import { useAuthContext } from 'src/auth/hooks';
import { useNavigate } from 'react-router';
// ----------------------------------------------------------------------
const icon = (name) => (
  <SvgColor src={ `/assets/icons/navbar/${name}.svg` } sx={ { width: 1, height: 1 } } />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dash'),
  user: icon('users'),
  role: icon('role'),
  admin: icon('admin'),
  settings: icon('settings'),
  communication: icon('communication'),
  prompt: icon('prompt'),
  database: icon('database'),
  reports: icon('reports'),
  cms: icon('cms'),
  product_update: icon('product_update'),
  support: icon('support'),
  invoice: icon('invoice'),
  apps: icon('apps'),
  crm: icon('crm'),
  technical: icon('technical'),
  demo: icon('demo'),
  sales: icon('sales'),
  market: icon('market'),
  lead: icon('lead'),
  salesteam: icon('salesteam'),
};

// ----------------------------------------------------------------------
export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();

  const navigate = useNavigate();

  const userPermissions = user?.roles?.flatMap((role) =>
    role?.permissions?.map((perm) => perm?.name)
  ) || [];

  // Check if any permission starts with a specific keyword
  const hasnestedPermission = (keyword) => userPermissions.some((perm) => perm.startsWith(keyword));
  const hasDashboardPermission = hasnestedPermission('dashboard');
  console.log("🚀 ~ useNavData ~ hasDashboardPermission:", hasDashboardPermission)


  useEffect(() => {
    if (hasDashboardPermission === false) {
      navigate('/dashboard/adminuser', { replace: true });
    }
  }, [])


  const filteredItems = (items) => {
    return items
      .map((item) => {
        if (item.children) {
          const visibleChildren = item.children.filter((child) => child.visible);
          if (visibleChildren.length > 0) {
            return { ...item, children: visibleChildren, visible: true };
          }
          return null;
        }
        return item.visible ? item : null;
      })
      .filter(Boolean);
  };
  const data = useMemo(
    () => [
      {
        subheader: t('overview'),
        items: filteredItems([
          {
            title: t('Dashboard'),
            path: hasDashboardPermission ? '/dashboard' : '/dashboard/adminuser',
            icon: ICONS.dashboard,
            visible: true, // Always visible, only path changes
          },
          {
            title: t('Ezelogs User'),
            path: '/dashboard/ezelogs-user',
            icon: ICONS.user,
            children: [
              {
                title: t('Registered User'),
                path: paths.dashboard.register_user,
                visible: hasnestedPermission('dashboard.registered_users'),
              },
              {
                title: t('Vendors Profile'),
                path: paths.dashboard.vendor_user,
                visible: hasnestedPermission('dashboard.vendors_profile'),
              },
            ],
          },
          {
            title: t('Role & Permission'),
            path: paths.dashboard.role_and_permision,
            icon: ICONS.role,
            visible: hasnestedPermission('role.list'),
          },

          {
            title: t('Admin User'),
            path: '/dashboard/admin-users',
            icon: ICONS.admin,
            children: [
              { title: t('Create New User'), path: paths.dashboard.create_new_user_admin_side, visible: hasnestedPermission('user_management.create') },
              { title: t('All Admin Users'), path: paths.dashboard.admin_all_users, visible: hasnestedPermission('user_management.list') },
              { title: t('Marketing'), path: paths.dashboard.admin_marketing, visible: hasnestedPermission('user_management.list') },
              { title: t('Leads'), path: paths.dashboard.admin_leads, visible: hasnestedPermission('user_management.list') },
              { title: t('Sales'), path: paths.dashboard.admin_sales, visible: hasnestedPermission('user_management.list') },
              { title: t('Demo'), path: paths.dashboard.admin_demo, visible: hasnestedPermission('user_management.list') },
              { title: t('Technical'), path: paths.dashboard.admin_technical, visible: hasnestedPermission('user_management.list') },
              { title: t('Others'), path: paths.dashboard.others_marketing, visible: hasnestedPermission('user_management.list') },
            ],
          },
          {
            title: t('Marketing Management'),
            path: '/dashboard/marketing-management',
            icon: ICONS.market,
            children: [
              { title: t('Dashboard'), path: paths.dashboard.marketing_dashboard, visible: hasnestedPermission('user_management.create') },
              { title: t('Marketing'), path: paths.dashboard.marketing_tools, visible: hasnestedPermission('user_management.list') },
              { title: t('Boards'), path: paths.dashboard.marketing_board, visible: hasnestedPermission('user_management.list') },
              { title: t('Time Line'), path: paths.dashboard.marketing_timeline, visible: hasnestedPermission('user_management.list') },
              { title: t('List'), path: paths.dashboard.marketing_list, visible: hasnestedPermission('user_management.list') },

            ],
          },
          {
            title: t('Product Packages/Plans'),
            path: '/dashboard/product-packages',
            icon: ICONS.salesteam,
            children: [
              { title: t('Module & Features'), path: paths.dashboard.products_packages_feature, visible: hasnestedPermission('packages.view') },
              { title: t('Plans'), path: paths.dashboard.products_packages_plans, visible: hasnestedPermission('packages.view') },
            ],
          },
          {
            title: t('Support Tickets'),
            path: paths.dashboard.support_ticket,
            icon: ICONS.support,
            visible: hasnestedPermission('support_tickets'),
          },
          {
            title: t('Product Updates'),
            path: '/dashboard/product-updates',
            icon: ICONS.product_update,
            children: [
              { title: t('Newsletter'), path: paths.dashboard.product_user_news_letter, visible: hasnestedPermission('product_updates.news_letter') },
              { title: t('Maintainance'), path: paths.dashboard.product_user_user_maintainance, visible: hasnestedPermission('product_updates.maintenance') },
              { title: t('Offers'), path: paths.dashboard.product_user_offers, visible: hasnestedPermission('product_updates.offers') },
              { title: t('Blogs'), path: paths.dashboard.product_user_blogs, visible: hasnestedPermission('product_updates.blogs') },
              { title: t('General'), path: paths.dashboard.product_user_general, visible: hasnestedPermission('product_updates.general') },
              { title: t('Product Features'), path: paths.dashboard.product_features, visible: hasnestedPermission('products_features.access') },
            ],
          },
          {
            title: t('Technical Database'),
            path: '/dashboard/technical-database',
            icon: ICONS.database,
            children: [
              { title: t('Cost Codes'), path: paths.dashboard.technical_cost_codes, visible: hasnestedPermission('technical_database.cost_code') },
              { title: t('Materials'), path: paths.dashboard.technical_materials, visible: hasnestedPermission('technical_database.material') },
              { title: t('Equipment'), path: paths.dashboard.technical_equipments, visible: hasnestedPermission('technical_database.equipments') },
              { title: t('Trades'), path: paths.dashboard.technical_trades, visible: hasnestedPermission('technical_database.trades') },
              { title: t('Trades/Worker Titles'), path: paths.dashboard.technical_trades_work_title, visible: hasnestedPermission('trades_work_title.access') },
              { title: t('Bids'), path: paths.dashboard.technical_bids, visible: hasnestedPermission('technical_database.bids') },
              { title: t('STT Folders'), path: paths.dashboard.technical_stt_folders, visible: hasnestedPermission('stt_folder.access') },
            ],
          },
             {
            title: t('CMS Management'),
            path: '/dashboard/cms-management',
            icon: ICONS.cms,
            children: [
              { title: t('Website Pages'), path: paths.dashboard.HomePage, visible: hasnestedPermission('') },
              { title: t('Assests'), path: paths.dashboard.Assests, visible: hasnestedPermission('') },

            ],
          },
        ]),
      },
    ],
    [ t, userPermissions ]
  );

  return data;

}
